import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { buscarCep } from "@/lib/cep";

const LIMITE_SEMANAL_PLATAFORMA = 12;
const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const LABEL_DIAS = {
  segunda: "Seg", terca: "Ter", quarta: "Qua", quinta: "Qui",
  sexta: "Sex", sabado: "Sáb", domingo: "Dom",
};
const LABEL_SERVICOS = {
  montagem: "Montagem", assistencia: "Assistência Técnica",
  revisao_30d: "Revisão após 30 dias", garantia: "Garantia",
  desmontagem: "Desmontagem", transferencia: "Transferência",
  mostruario: "Mostruário", instalacao: "Instalação", outros: "Outros",
};

export default function MontadorConfigOperacional() {
  const [meta, setMeta] = useState({ raios_disponiveis: [], servicos_disponiveis: [], dias_semana: [] });
  const [form, setForm] = useState({
    cidade_principal: "", estado_principal: "", cep_referencia: "",
    atende_toda_cidade: true, bairros: [],
    raio_km: 30,
    capacidade_diaria: {}, capacidade_semanal: 0,
    servicos_desejados: [],
    aceite_cobertura: false,
  });
  const [municipiosUF, setMunicipiosUF] = useState([]);
  const [bairrosDisp, setBairrosDisp] = useState(null); // null=não carregado, {loading, lista}
  const [buscaBairro, setBuscaBairro] = useState("");
  const [bairroInput, setBairroInput] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [cfgExistente, setCfgExistente] = useState(false);
  const [erroAceite, setErroAceite] = useState(false);
  const aceiteRef = useRef(null);

  useEffect(() => {
    api.get("/montador/configuracoes-operacionais").then(r => {
      const d = r.data;
      setMeta({
        raios_disponiveis: d.raios_disponiveis || [5, 10, 15, 20, 30],
        servicos_disponiveis: d.servicos_disponiveis || [],
        dias_semana: d.dias_semana || [],
      });
      if (d.status !== "nao_cadastrado") {
        setCfgExistente(true);
        setForm({
          cidade_principal: d.cidade_principal || "",
          estado_principal: d.estado_principal || "",
          cep_referencia: d.cep_referencia || "",
          atende_toda_cidade: d.atende_toda_cidade ?? true,
          bairros: d.bairros || [],
          raio_km: d.raio_km || 30,
          capacidade_diaria: d.capacidade_diaria || {},
          capacidade_semanal: d.capacidade_semanal || 0,
          servicos_desejados: d.servicos_desejados || [],
          aceite_cobertura: false, // sempre exige reaceite ao atualizar
        });
        if (d.estado_principal) carregarMunicipios(d.estado_principal);
        if (d.estado_principal && d.cidade_principal) carregarBairros(d.estado_principal, d.cidade_principal);
      }
    });
    // eslint-disable-next-line
  }, []);

  const carregarMunicipios = (uf) => {
    if (!uf || uf.length !== 2) return;
    api.get(`/localidades/${uf.toUpperCase()}/municipios`)
      .then(r => setMunicipiosUF(r.data))
      .catch(() => setMunicipiosUF([]));
  };

  const carregarBairros = (uf, cidade) => {
    if (!uf || !cidade) return;
    setBairrosDisp({ loading: true, lista: [] });
    api.get(`/localidades/${uf.toUpperCase()}/${encodeURIComponent(cidade)}/bairros`)
      .then(r => setBairrosDisp({ loading: false, lista: r.data.bairros || [] }))
      .catch(() => setBairrosDisp({ loading: false, lista: [] }));
  };

  const setUF = (uf) => {
    setForm(f => ({ ...f, estado_principal: uf, cidade_principal: "", bairros: [] }));
    setBairrosDisp(null);
    carregarMunicipios(uf);
  };

  const setCidade = (cidade) => {
    setForm(f => ({ ...f, cidade_principal: cidade, bairros: [] }));
    if (municipiosUF.includes(cidade)) carregarBairros(form.estado_principal, cidade);
  };

  const [buscandoCep, setBuscandoCep] = useState(false);
  const onCepChange = async (v) => {
    setForm((f) => ({ ...f, cep_referencia: v }));
    const limpo = v.replace(/\D/g, "");
    if (limpo.length !== 8) return;
    setBuscandoCep(true);
    const end = await buscarCep(limpo);
    setBuscandoCep(false);
    if (end) {
      setForm((f) => ({ ...f, cep_referencia: v, cidade_principal: end.cidade, estado_principal: end.estado, bairros: [] }));
      carregarMunicipios(end.estado);
      carregarBairros(end.estado, end.cidade);
      toast.success(`Cidade preenchida: ${end.cidade}/${end.estado}`);
    } else {
      toast.info("CEP não encontrado — selecione UF e cidade manualmente");
    }
  };

  const setCap = (dia, v) => {
    const nova = { ...form.capacidade_diaria, [dia]: Math.max(0, parseInt(v) || 0) };
    const total = Object.values(nova).reduce((s, n) => s + (n || 0), 0);
    setForm({ ...form, capacidade_diaria: nova, capacidade_semanal: total });
  };

  const toggleServico = (s) => {
    const arr = form.servicos_desejados.includes(s)
      ? form.servicos_desejados.filter(x => x !== s)
      : [...form.servicos_desejados, s];
    setForm({ ...form, servicos_desejados: arr });
  };

  const toggleBairro = (b) => {
    setForm(f => ({
      ...f,
      bairros: f.bairros.includes(b) ? f.bairros.filter(x => x !== b) : [...f.bairros, b],
    }));
  };

  const addBairroManual = () => {
    const b = bairroInput.trim();
    if (!b || form.bairros.includes(b)) return;
    setForm({ ...form, bairros: [...form.bairros, b] });
    setBairroInput("");
  };

  const salvar = async () => {
    if (!form.cidade_principal || !form.estado_principal) { toast.error("Selecione UF e cidade"); return; }
    if (!form.atende_toda_cidade && form.bairros.length === 0) { toast.error("Selecione ao menos 1 bairro"); return; }
    if (form.servicos_desejados.length === 0) { toast.error("Selecione ao menos 1 serviço"); return; }
    if (!form.aceite_cobertura) {
      setErroAceite(true);
      aceiteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error("Falta 1 passo: marque o aceite de cobertura destacado em vermelho para salvar");
      return;
    }
    setSalvando(true);
    try {
      await api.post("/montador/configuracoes-operacionais", form);
      toast.success("Configurações salvas · aceite de cobertura registrado");
      setCfgExistente(true);
      setErroAceite(false);
      setForm(f => ({ ...f, aceite_cobertura: false }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  const listaBairros = bairrosDisp?.lista || [];
  const bairrosFiltrados = buscaBairro
    ? listaBairros.filter(b => b.toLowerCase().includes(buscaBairro.toLowerCase()))
    : listaBairros;

  return (
    <>
      <div className="max-w-4xl mx-auto p-6 space-y-5">
        {/* Alertas sonoros */}
        <Card title="Alertas sonoros" subtitle="Toca a assinatura sonora do MontaJá quando uma nova montagem entrar para você.">
          <label className="flex items-center gap-3 text-sm font-medium cursor-pointer select-none">
            <input type="checkbox" defaultChecked={localStorage.getItem("mj_som_alertas") !== "0"}
              onChange={(e) => localStorage.setItem("mj_som_alertas", e.target.checked ? "1" : "0")}
              className="h-4 w-4 accent-[#FF6700]" data-testid="co-som-alertas" />
            Som de nova montagem ativado
          </label>
        </Card>
        {/* 1. Cidade Principal */}
        <Card title="1 · Cidade principal de atendimento" subtitle="Selecione (sem digitação livre) para evitar erros de grafia. Pode atualizar quando quiser.">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">UF</Label>
              <select value={form.estado_principal}
                onChange={(e) => setUF(e.target.value)}
                className="mt-1 w-full h-10 border border-border bg-white px-2 text-sm"
                data-testid="co-uf">
                <option value="">UF…</option>
                {UFS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Cidade</Label>
              <Input value={form.cidade_principal} list="co-lista-cidades"
                onChange={(e) => setCidade(e.target.value)}
                onBlur={() => { if (form.cidade_principal && !bairrosDisp) carregarBairros(form.estado_principal, form.cidade_principal); }}
                placeholder={form.estado_principal ? "Comece a digitar e selecione…" : "Selecione a UF primeiro"}
                disabled={!form.estado_principal}
                data-testid="co-cidade" />
              <datalist id="co-lista-cidades">
                {municipiosUF.map(m => <option key={m} value={m} />)}
              </datalist>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">CEP de referência (opcional · preenche cidade/UF automaticamente){buscandoCep && " · buscando…"}</Label>
            <Input value={form.cep_referencia} placeholder="00000-000"
              onChange={(e) => onCepChange(e.target.value)}
              className="max-w-[200px]" data-testid="co-cep" />
          </div>
        </Card>

        {/* 2. Atende toda cidade ou bairros */}
        <Card title="2 · Cobertura da cidade" subtitle="Atende toda a cidade ou seleciona bairros específicos?">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className={`border-2 p-3 cursor-pointer text-sm ${form.atende_toda_cidade ? "border-[#142766] bg-[#142766]/5" : "border-border"}`}
              data-testid="co-toda-sim">
              <input type="radio" checked={form.atende_toda_cidade}
                onChange={() => setForm({ ...form, atende_toda_cidade: true })} className="sr-only" />
              <div className="font-bold">Toda a cidade</div>
              <div className="text-xs text-muted-foreground mt-1">Recebe oportunidades em qualquer bairro</div>
            </label>
            <label className={`border-2 p-3 cursor-pointer text-sm ${!form.atende_toda_cidade ? "border-[#142766] bg-[#142766]/5" : "border-border"}`}
              data-testid="co-toda-nao">
              <input type="radio" checked={!form.atende_toda_cidade}
                onChange={() => { setForm({ ...form, atende_toda_cidade: false }); if (!bairrosDisp && form.cidade_principal) carregarBairros(form.estado_principal, form.cidade_principal); }} className="sr-only" />
              <div className="font-bold">Apenas bairros selecionados</div>
              <div className="text-xs text-muted-foreground mt-1">Restringe a regiões específicas</div>
            </label>
          </div>
          {!form.atende_toda_cidade && (
            <div>
              <Label className="text-xs">Bairros atendidos ({form.bairros.length} selecionado(s))</Label>
              {bairrosDisp?.loading && (
                <div className="mt-2 text-xs text-muted-foreground animate-pulse">Carregando bairros de {form.cidade_principal}…</div>
              )}
              {!bairrosDisp?.loading && listaBairros.length > 0 && (
                <div className="mt-2" data-testid="co-bairros-picker">
                  <Input value={buscaBairro} onChange={(e) => setBuscaBairro(e.target.value)}
                    placeholder={`Buscar bairro em ${form.cidade_principal}…`} className="h-8 text-xs max-w-60"
                    data-testid="co-bairros-busca" />
                  <div className="mt-2 max-h-44 overflow-auto flex flex-wrap gap-1">
                    {bairrosFiltrados.map(b => {
                      const on = form.bairros.includes(b);
                      return (
                        <button key={b} type="button" onClick={() => toggleBairro(b)}
                          className={`px-2 py-1 text-[11px] font-semibold transition-colors ${on
                            ? "bg-[#142766] text-white"
                            : "border border-border text-muted-foreground hover:border-[#142766]"}`}
                          data-testid={`co-bairro-${b}`}>
                          {b}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {!bairrosDisp?.loading && listaBairros.length === 0 && (
                <div className="mt-2">
                  <p className="text-[11px] text-muted-foreground mb-1">Lista oficial indisponível para esta cidade — digite os bairros manualmente:</p>
                  <div className="flex gap-2">
                    <Input value={bairroInput} onChange={(e) => setBairroInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBairroManual())}
                      placeholder="Digite e pressione Enter" data-testid="co-bairro-input" />
                    <Button onClick={addBairroManual} variant="outline" className="btn-sharp" data-testid="co-bairro-add">+ Adicionar</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.bairros.map(b => (
                      <span key={b} className="bg-secondary px-2 py-1 text-xs flex items-center gap-1" data-testid={`co-bairro-chip-${b}`}>
                        {b}
                        <button onClick={() => toggleBairro(b)} className="text-muted-foreground hover:text-foreground">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* 3. Raio de atendimento */}
        <Card title="3 · Raio de atendimento" subtitle="Até onde você se desloca a partir da sua cidade/CEP de referência?">
          <div className="grid grid-cols-7 gap-2">
            {meta.raios_disponiveis.map(r => (
              <button key={r} onClick={() => setForm({ ...form, raio_km: r })}
                className={`border-2 p-3 text-sm font-bold transition ${form.raio_km === r ? "border-[#142766] bg-[#142766] text-white" : "border-border hover:border-foreground/40"}`}
                data-testid={`co-raio-${r}`}>
                {r}km
              </button>
            ))}
          </div>
        </Card>

        {/* 4. Capacidade */}
        <Card title="4 · Capacidade operacional" subtitle="Quantos atendimentos por dia você consegue executar? O sistema NUNCA ultrapassará esses limites.">
          <div className="grid grid-cols-7 gap-2">
            {meta.dias_semana.map(d => (
              <div key={d}>
                <Label className="text-[10px] text-center block">{LABEL_DIAS[d]}</Label>
                <Input type="number" min="0" max="20"
                  value={form.capacidade_diaria[d] || 0}
                  onChange={(e) => setCap(d, e.target.value)}
                  className="mt-1 text-center font-mono"
                  data-testid={`co-cap-${d}`} />
              </div>
            ))}
          </div>
          <div className="mt-3 text-right text-sm">
            Total semanal: <strong className="font-mono text-lg" data-testid="co-cap-total">{form.capacidade_semanal}</strong> atendimentos
            {form.capacidade_semanal > LIMITE_SEMANAL_PLATAFORMA && (
              <div className="text-xs text-[#B45309] font-semibold mt-1" data-testid="co-cap-aviso">
                ⚠ A distribuição automática da plataforma é limitada a {LIMITE_SEMANAL_PLATAFORMA} atendimentos/semana.
              </div>
            )}
          </div>
        </Card>

        {/* 5. Serviços desejados */}
        <Card title="5 · Serviços desejados" subtitle="Quais tipos de serviço você quer receber? Pode ajustar a qualquer momento.">
          <div className="grid md:grid-cols-3 gap-2">
            {meta.servicos_disponiveis.map(s => {
              const ativo = form.servicos_desejados.includes(s);
              return (
                <button key={s} onClick={() => toggleServico(s)}
                  className={`border-2 p-3 text-sm text-left transition ${ativo ? "border-[#00A859] bg-[#00A859]/5" : "border-border hover:border-foreground/40"}`}
                  data-testid={`co-serv-${s}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg ${ativo ? "text-[#00A859]" : "text-muted-foreground"}`}>{ativo ? "✓" : "○"}</span>
                    <span className="font-bold">{LABEL_SERVICOS[s]}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* 6. Aceite */}
        <div ref={aceiteRef}
          className={`border-2 p-5 transition-colors ${erroAceite && !form.aceite_cobertura
            ? "border-[#E60000] bg-red-50 animate-pulse"
            : "border-[#FFCC00] bg-[#FFCC00]/10"}`}
          data-testid="co-aceite-box">
          {erroAceite && !form.aceite_cobertura && (
            <div className="text-xs font-extrabold uppercase text-[#B91C1C] mb-2">⚠ Marque abaixo para conseguir salvar</div>
          )}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.aceite_cobertura}
              onChange={(e) => { setForm({ ...form, aceite_cobertura: e.target.checked }); if (e.target.checked) setErroAceite(false); }}
              className="mt-1" data-testid="co-aceite" />
            <div className="text-sm">
              <strong>Confirmo que desejo receber atendimentos dentro da área selecionada acima</strong>, dentro da minha capacidade declarada
              e nos tipos de serviço que escolhi. Entendo que este aceite registra IP, dispositivo e horário no log jurídico imutável da plataforma.
              <span className="block mt-1 text-xs text-muted-foreground">Por segurança jurídica, o aceite é pedido novamente a cada atualização.</span>
            </div>
          </label>
        </div>

        <Button onClick={salvar} disabled={salvando}
          className="w-full h-14 btn-sharp brand-bg text-white text-lg" data-testid="co-salvar">
          {salvando ? "Salvando…" : (cfgExistente ? "Atualizar configurações" : "Salvar configurações")}
        </Button>
      </div>
    </>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="border border-border bg-white p-5">
      <div className="label-cap text-[#142766]">{title}</div>
      <p className="text-xs text-muted-foreground mt-1 mb-3">{subtitle}</p>
      {children}
    </div>
  );
}
